import { DotsVerticalIcon } from '@radix-ui/react-icons';
import { Label } from '@radix-ui/react-label';
import {
  Avatar,
  Badge,
  Box,
  Card,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  Skeleton,
} from '@radix-ui/themes';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { CommonMenu } from 'components/common/menu';
import { CommonHorizontalBreaksBadge } from 'components/common/pitch-lists/h-breaks-badge';
import { CommonPlateLocationGraphic } from 'components/common/pitch-lists/plate-location-graphic';
import { CommonPriorityBadge } from 'components/common/pitch-lists/priority-badge';
import { CommonSpeedBadge } from 'components/common/pitch-lists/speed-badge';
import { CommonSpinBadge } from 'components/common/pitch-lists/spin-badge';
import { CommonTrainingStatusIcon } from 'components/common/pitch-lists/training-status-icon';
import { CommonVerticalBreaksBadge } from 'components/common/pitch-lists/v-breaks-badge';
import { PitchOnDemandBadge } from 'components/common/pitch-on-demand-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { convertTableToMenu } from 'components/common/table/actions-cells';
import { CommonTooltip } from 'components/common/tooltip';
import { QueueIndicator } from 'components/sections/pitch-list/pitch-card/queue-indicator';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { AimingContext } from 'contexts/aiming.context';
import { ContextMenuContext } from 'contexts/context-menu.context';
import { FiringContext } from 'contexts/firing/context';
import { CheckedContext } from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { IgnoreAutoFireMode } from 'enums/firing';
import { ResetPlateMode } from 'enums/machine.enums';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useOnContextMenu } from 'hooks/useOnContextMenu';
import { RegexFilter } from 'interfaces/forms/text';
import { TrainPitchesFn } from 'interfaces/function-types';
import { QueueType } from 'interfaces/i-queue-mode';
import { ITableAction } from 'interfaces/tables/action';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { debounce, isNil } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const AUTO_REFRESH_ON_TRAIN = true;
const FREQ_DEBOUNCE_MS = 1000;

// MARK: TrainingBadge
const TrainingBadge = (props: { pitch: IPitch; loading: boolean }) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  const { machine } = useContext(MachineContext);
  const { last_hardware_changed } = machine;

  // Subscribe ONLY to this pitch's TMS slice. Updates to other entries in
  // tmsDict don't change the selected reference, so this card won't rerender
  // when other pitches' training state changes.
  const tms = useTrainedMsStore((s) => s.tmsDict[props.pitch.bs_hash]);

  if (props.pitch.debugMs) {
    return (
      <Skeleton loading={!!props.loading}>
        <Box>
          <Badge>Debug</Badge>
        </Box>
      </Skeleton>
    );
  }

  if (onDemand) {
    return (
      <Skeleton loading={!!props.loading}>
        <Box>
          <PitchOnDemandBadge odms={props.pitch._mx_odms} />
        </Box>
      </Skeleton>
    );
  }

  const canRefresh = !tms || (tms.shotIDs.length === 0 && !tms.skipRefresh);

  return (
    <Skeleton loading={!!props.loading}>
      <Flex align="center">
        <CommonTooltip
          trigger={
            <Box>
              <CommonTrainingStatusIcon
                tms={tms}
                last_hardware_changed={last_hardware_changed}
                autoRefresh={AUTO_REFRESH_ON_TRAIN}
                canRefresh={canRefresh}
              />
            </Box>
          }
          text_md={PitchListHelper.getTrainedStatusTooltip({
            tms: tms,
            last_hardware_changed: last_hardware_changed,
          })}
        />
      </Flex>
    </Skeleton>
  );
};

// MARK: PitchCard
export const PitchCard = (props: {
  pitch: IPitch;
  targetActions: ITableAction[];
  onTrainFn: TrainPitchesFn;
  loading?: boolean;
}) => {
  const { setAndSendForBP } = useContext(AimingContext);
  const { safeSetIgnoreAf } = useContext(FiringContext);

  const filteredPitches = usePitchListStore((s) => s.filteredPitches);
  const queueType = usePitchListStore((s) => s.queueType);
  const selectedPitch = usePitchListStore((s) => s.selectedPitch);

  const { lastCheckedIndex, getChecked, setChecked, setLastCheckedIndex } =
    useContext(CheckedContext);

  const debouncedUpdatePitches = useCallback(
    debounce(usePitchListStore.getState().updatePitches, FREQ_DEBOUNCE_MS),
    []
  );

  const [frequency, setFrequency] = useState<number | undefined>(
    props.pitch.frequency
  );

  useEffect(() => {
    if (!props.pitch._id) {
      return;
    }

    if (props.pitch.frequency === frequency) {
      return;
    }

    debouncedUpdatePitches({
      payloads: [
        {
          _id: props.pitch._id,
          frequency: frequency,
        },
      ],
    });
  }, [props.pitch._id, props.pitch.frequency, frequency]);

  const isChecked = getChecked(props.pitch._id);

  const checkboxID = `cb-${props.pitch._id}`;

  const onChangeChecked = useCallback(() => {
    // Toggling the checkbox is the only responsibility here. The queue is
    // derived centrally from the selection order (see PitchListSidebarControls),
    // so we no longer mutate queuePitchIDs from individual cards.
    const nextChecked = !isChecked;

    setChecked([props.pitch._id], nextChecked);
    setLastCheckedIndex(
      filteredPitches.findIndex((p) => p._id === props.pitch._id)
    );
  }, [
    isChecked,
    props.pitch._id,
    setChecked,
    setLastCheckedIndex,
    filteredPitches,
  ]);

  const menuActions = useMemo(
    () =>
      props.targetActions
        .map((a) => convertTableToMenu(a, props.pitch))
        .filter((m) => !m.invisible),
    [props.targetActions, props.pitch]
  );

  const { setTarget } = useContext(ContextMenuContext);

  const { isLongPressing, onContextMenuHandlers } = useOnContextMenu({
    longPressEnabled: true,
    onContextMenu: () => {
      // the small delay allows for the context menu trigger for the body (which clears any previous selection) to go through before this happens
      setTimeout(() => {
        setTarget(props.pitch);
      }, 100);
    },
  });

  const classNames = useMemo(
    () =>
      StringHelper.classNames([
        'PitchCard cursor-pointer no-background no-border',
        isLongPressing ? 'select-none' : undefined,
      ]),
    [isLongPressing]
  );

  return (
    <ErrorBoundary identifier="PitchCard">
      <Card
        data-active={selectedPitch?._id === props.pitch._id}
        data-checked={isChecked}
        className={classNames}
        style={{ marginBottom: 'var(--space-1)', userSelect: 'none' }}
        onClick={async (event) => {
          const { pitch } = props;

          const index = filteredPitches.findIndex((p) => p._id === pitch._id);

          const handleShiftClick = () => {
            if (isNil(lastCheckedIndex)) {
              return;
            }

            const iStart = Math.min(lastCheckedIndex, index);
            const iEnd = Math.max(lastCheckedIndex, index);

            const ids = filteredPitches
              .filter((_, i) => i >= iStart && i <= iEnd)
              .map((m) => m._id);

            const nextValue = !getChecked(pitch._id);

            setChecked(ids, nextValue);
            setLastCheckedIndex(index);

            return;
          };

          // This is the first row that was checked so no need to bulk select yet
          // User does not need to shift-click on the first selection
          setLastCheckedIndex(index);

          if (event.shiftKey) {
            handleShiftClick();
          }

          safeSetIgnoreAf(IgnoreAutoFireMode.Permanent);

          usePitchListStore.getState().setSelectedPitch(pitch);

          setAndSendForBP({
            pitch: pitch,
            resetPlate: ResetPlateMode.PitchBackup,
            sendConfig: undefined,
          });
        }}
        {...onContextMenuHandlers}
      >
        <Flex align="center">
          {/* checkbox */}
          <Box mr="2">
            <Label
              htmlFor={checkboxID}
              className="CheckboxWrapperLabel"
              onClick={onChangeChecked}
            >
              <Skeleton loading={!!props.loading}>
                <Checkbox
                  id={checkboxID}
                  className="cursor-pointer"
                  checked={isChecked}
                  onClick={onChangeChecked}
                />
              </Skeleton>
            </Label>
          </Box>

          {/* queue index */}
          <Skeleton loading={!!props.loading}>
            <Flex align="center" mr="3">
              <QueueIndicator pitchID={props.pitch._id} />
            </Flex>
          </Skeleton>

          {/* pitch type */}
          <Skeleton loading={!!props.loading}>
            <Flex align="center" mr="4">
              <Avatar
                color={RADIX.COLOR.NEUTRAL}
                size="3"
                fallback={props.pitch.type ?? '??'}
                style={{
                  color: 'var(--accent-a11)',
                  backgroundColor: 'var(--gray-a2)',
                  boxShadow: '0 0 0 1px var(--gray-a6)',
                }}
              />
            </Flex>
          </Skeleton>

          {/* pitch details */}
          <Flex
            direction="column"
            gap="1"
            flexGrow="1"
            minWidth="0"
            overflow="hidden"
            mr="4"
          >
            {/* name + training status */}
            <Skeleton loading={!!props.loading} width="256px">
              <Heading size="2">{props.pitch.name}</Heading>
            </Skeleton>
            {/* badges */}
            <Flex gap="1" pr="3" wrap="nowrap" overflow="hidden">
              <TrainingBadge pitch={props.pitch} loading={!!props.loading} />

              <Skeleton loading={!!props.loading}>
                <Box>
                  <PitcherHandBadge
                    value={BallHelper.getPitcherHand(props.pitch.bs.px)}
                  />
                </Box>
              </Skeleton>
              <Skeleton loading={!!props.loading}>
                <Box>
                  <CommonPriorityBadge priority={props.pitch.priority} />
                </Box>
              </Skeleton>
              <Skeleton loading={!!props.loading}>
                <Box>
                  <CommonSpeedBadge traj={props.pitch.traj} />
                </Box>
              </Skeleton>
              <Skeleton loading={!!props.loading}>
                <Box>
                  <CommonSpinBadge bs={props.pitch.bs} />
                </Box>
              </Skeleton>
              {props.pitch.priority === BuildPriority.Breaks &&
                props.pitch.breaks && (
                  <Skeleton loading={!!props.loading}>
                    <Box>
                      <CommonVerticalBreaksBadge breaks={props.pitch.breaks} />
                    </Box>
                  </Skeleton>
                )}
              {props.pitch.priority === BuildPriority.Breaks &&
                props.pitch.breaks && (
                  <Skeleton loading={!!props.loading}>
                    <Box>
                      <CommonHorizontalBreaksBadge
                        breaks={props.pitch.breaks}
                      />
                    </Box>
                  </Skeleton>
                )}
            </Flex>
          </Flex>

          <Flex flexShrink="0">
            <Skeleton loading={!!props.loading}>
              <Flex align="center">
                <CommonPlateLocationGraphic pitch={props.pitch} />
              </Flex>
            </Skeleton>

            {queueType === QueueType.ShuffleEach && (
              <Skeleton loading={!!props.loading}>
                <Flex align="center" ml="3" style={{ width: '60px' }}>
                  <CommonTextInput
                    id={`freq-${props.pitch._id}`}
                    name={`freq-${props.pitch._id}`}
                    value={frequency?.toString() ?? ''}
                    title="common.frequency"
                    regex={RegexFilter.Numeric}
                    onNumericChange={setFrequency}
                  />
                </Flex>
              </Skeleton>
            )}

            {!props.loading && (
              <Flex align="center" ml="3">
                <CommonMenu
                  title="Actions"
                  trigger={
                    <IconButton
                      id={`actions-${props.pitch._id}`}
                      color={RADIX.COLOR.NEUTRAL}
                      className="btn-floating"
                      size={RADIX.BUTTON.SIZE.TABLE}
                      variant={RADIX.BUTTON.VARIANT.TABLE}
                    >
                      <DotsVerticalIcon />
                    </IconButton>
                  }
                  actions={menuActions}
                  skipSort
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Card>
    </ErrorBoundary>
  );
};
